/**
 * Logger - Environment-aware logging system
 *
 * Replaces raw console.log statements with intelligent logging
 * that respects environment (dev vs production)
 */

import type { LogLevel, LogEntry, LogLevels } from '../types';

const LOG_LEVELS: LogLevels = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
} as const;

class Logger {
  private level: number;
  private logBuffer: LogEntry[];
  private maxBufferSize: number;

  constructor() {
    // Set level based on environment
    // Development: show everything (DEBUG+)
    // Production: only errors (ERROR+)
    this.level = this.getLogLevel();

    // Optionally persist logs in production for debugging
    this.logBuffer = [];
    this.maxBufferSize = 100;
  }

  private getLogLevel(): number {
    // Vite sets import.meta.env.DEV in development
    if (import.meta.env.DEV) {
      return LOG_LEVELS.DEBUG;
    }

    // Production: only errors
    return LOG_LEVELS.ERROR;
  }

  /**
   * Debug level logging
   * Use for detailed diagnostic information
   */
  debug(...args: unknown[]): void {
    if (this.level <= LOG_LEVELS.DEBUG) {
      console.log('%c[DEBUG]', 'color: #9E9E9E', ...args);
      this.bufferLog('DEBUG', args);
    }
  }

  /**
   * Info level logging
   * Use for general informational messages
   */
  info(...args: unknown[]): void {
    if (this.level <= LOG_LEVELS.INFO) {
      console.info('%c[INFO]', 'color: #2196F3', ...args);
      this.bufferLog('INFO', args);
    }
  }

  /**
   * Warning level logging
   * Use for potentially harmful situations
   */
  warn(...args: unknown[]): void {
    if (this.level <= LOG_LEVELS.WARN) {
      console.warn('%c[WARN]', 'color: #FF9800', ...args);
      this.bufferLog('WARN', args);
    }
  }

  /**
   * Error level logging
   * Use for error events that might still allow the app to continue
   */
  error(...args: unknown[]): void {
    if (this.level <= LOG_LEVELS.ERROR) {
      console.error('%c[ERROR]', 'color: #F44336', ...args);
      this.bufferLog('ERROR', args);
    }
  }

  /**
   * Buffer logs for debugging in production
   * (useful for post-mortem analysis)
   */
  private bufferLog(level: LogLevel, args: unknown[]): void {
    this.logBuffer.push({
      level,
      message: args,
      timestamp: new Date().toISOString()
    });

    // Keep buffer size manageable
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }
  }

  /**
   * Get buffered logs (useful for bug reports)
   */
  getBufferedLogs(): LogEntry[] {
    return this.logBuffer;
  }

  /**
   * Export logs as JSON (for support/debugging)
   */
  exportLogs(): void {
    const logs = this.getBufferedLogs();
    const json = JSON.stringify(logs, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `nalivox-logs-${Date.now()}.json`;
    link.click();

    URL.revokeObjectURL(url);
  }

  /**
   * Clear log buffer
   */
  clearBuffer(): void {
    this.logBuffer = [];
  }
}

// Create singleton instance
const logger = new Logger();

// Export both the instance and the class
export default logger;
export { Logger, LOG_LEVELS };
